import { baseApi, ENDPOINTS } from '@/shared/api';
import {
    clearStoredReferralCode,
    getStoredReferralCode,
} from '@/shared/lib/referral';
import type { SessionData } from '@/shared/types/session';

import type { Credentials } from '../model/types/credentials';
import type { ResetPasswordParams } from '../model/types/reset-password-params';
import type { SignUpBody } from '../model/types/sign-up.body';

const authApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        signIn: build.mutation<SessionData, Credentials>({
            query: (body) => ({
                url: ENDPOINTS.auth.signIn,
                method: 'POST',
                body,
            }),
        }),
        signUp: build.mutation<void, SignUpBody>({
            query: (body) => ({
                url: ENDPOINTS.auth.signUp,
                method: 'POST',
                body,
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
                // Attribution recorded server-side: the code is spent.
                clearStoredReferralCode();
            },
        }),
        confirmEmail: build.query<
            void,
            Pick<Credentials, 'email'> & { otp: string }
        >({
            query: (query) => ({
                url: ENDPOINTS.auth.confirmEmail,
                params: query,
            }),
        }),
        initiatePasswordRecovery: build.mutation<
            void,
            Pick<Credentials, 'email'>
        >({
            query: (body) => ({
                url: ENDPOINTS.auth.initiatePasswordRecovery,
                method: 'POST',
                body,
            }),
        }),
        resetPassword: build.mutation<
            void,
            Pick<Credentials, 'password'> & ResetPasswordParams
        >({
            query: ({ password, ...body }) => ({
                url: ENDPOINTS.auth.resetPassword,
                method: 'PATCH',
                body: { ...body, new_password: password },
            }),
        }),
        checkResetPasswordParams: build.mutation<void, ResetPasswordParams>({
            query: (body) => ({
                url: ENDPOINTS.auth.checkResetPasswordParams,
                method: 'POST',
                body,
            }),
        }),
        google: build.mutation<SessionData, string>({
            query: (code: string) => ({
                url: ENDPOINTS.auth.google,
                method: 'POST',
                body: { code, ref: getStoredReferralCode() },
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
                clearStoredReferralCode();
            },
        }),
        refreshTokens: build.mutation<SessionData, string>({
            query: (refreshToken: string) => ({
                url: ENDPOINTS.auth.refresh,
                method: 'POST',
                body: { refresh: refreshToken },
            }),
        }),
    }),
});

export const {
    useSignInMutation,
    useSignUpMutation,
    useInitiatePasswordRecoveryMutation,
    useCheckResetPasswordParamsMutation,
    useResetPasswordMutation,
    useLazyConfirmEmailQuery,
    useGoogleMutation,
    useRefreshTokensMutation,
} = authApi;

export const signInFulfilledMatcher = authApi.endpoints.signIn.matchFulfilled;
export const googleFulfilledMatcher = authApi.endpoints.google.matchFulfilled;
